import { useState } from 'react';
import type { PipelineDefinition } from 'edge-stream-js-react';
import { ErrorBoundary } from './components';
import { useRegistryManager } from './RegistryManagerContext';

type DirectionFilter = 'all' | 'incoming' | 'outgoing' | 'bidirectional';

function DirectionBadge({ direction }: { direction: string }) {
  const colors: Record<string, { bg: string; color: string }> = {
    incoming:      { bg: '#dbeafe', color: '#1d4ed8' },
    outgoing:      { bg: '#dcfce7', color: '#16a34a' },
    bidirectional: { bg: '#fef3c7', color: '#d97706' },
  };
  const style = colors[direction] ?? { bg: '#f3f4f6', color: '#374151' };
  return (
    <span
      style={{
        padding: '2px 10px',
        borderRadius: '12px',
        fontSize: '12px',
        fontWeight: 600,
        background: style.bg,
        color: style.color,
        display: 'inline-block',
      }}
    >
      {direction}
    </span>
  );
}

function ImpactBadge({ level }: { level?: string }) {
  if (!level) return null;
  const colors: Record<string, { bg: string; color: string }> = {
    critical: { bg: '#fee2e2', color: '#dc2626' },
    high:     { bg: '#ffedd5', color: '#ea580c' },
    medium:   { bg: '#fef9c3', color: '#ca8a04' },
    low:      { bg: '#f0fdf4', color: '#16a34a' },
  };
  const style = colors[level] ?? { bg: '#f3f4f6', color: '#374151' };
  return (
    <span
      style={{
        padding: '2px 8px',
        borderRadius: '8px',
        fontSize: '11px',
        background: style.bg,
        color: style.color,
      }}
    >
      {level}
    </span>
  );
}

function SourceBadge({ source }: { source?: string }) {
  if (!source) return null;
  return (
    <span style={{
      padding: '2px 8px',
      borderRadius: '8px',
      fontSize: '11px',
      background: '#ede9fe',
      color: '#7c3aed',
    }}>
      {source}
    </span>
  );
}

function PipelineCard({ definition }: { definition: PipelineDefinition }) {
  const { metadata } = definition;
  return (
    <div
      style={{
        padding: '20px',
        background: 'rgba(15, 23, 42, 0.95)',
        borderRadius: '12px',
        border: '1px solid rgba(58, 140, 69, 0.4)',
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
      }}
    >
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
        <DirectionBadge direction={metadata.direction} />
        <ImpactBadge level={metadata.impactLevel} />
        <SourceBadge source={metadata.source} />
      </div>

      {/* Name + ID */}
      <div>
        <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 700, color: '#f8fafc' }}>
          {metadata.name}
        </h3>
        <code style={{ fontSize: '11px', color: '#94a3b8', opacity: 0.7 }}>
          {metadata.id}
        </code>
      </div>

      {/* Description */}
      {metadata.description && (
        <p style={{ margin: 0, fontSize: '13px', color: '#94a3b8', lineHeight: 1.5 }}>
          {metadata.description}
        </p>
      )}

      {/* Footer: version, author, tags */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center', marginTop: 'auto' }}>
        {metadata.version && (
          <span style={{ fontSize: '12px', color: '#94a3b8' }}>
            v{metadata.version}
          </span>
        )}
        {metadata.author && (
          <span style={{ fontSize: '12px', color: '#94a3b8' }}>
            by {metadata.author}
          </span>
        )}
        {metadata.tags?.map((tag: string) => (
          <span
            key={tag}
            style={{
              padding: '2px 8px',
              background: 'rgba(58, 140, 69, 0.15)',
              borderRadius: '6px',
              fontSize: '11px',
              color: '#f8fafc',
            }}
          >
            {tag}
          </span>
        ))}
      </div>

      {/* Hook count */}
      <div style={{ fontSize: '12px', color: '#94a3b8', borderTop: '1px solid rgba(58, 140, 69, 0.2)', paddingTop: '8px' }}>
        Hooks: {definition.createHooks().length}
        {definition.createSubscribers
          ? ` · Subscribers: ${definition.createSubscribers().length}`
          : ' · Subscribers: default'}
      </div>
    </div>
  );
}

export function PipelinesPage(): React.ReactElement {
  const manager = useRegistryManager();
  const allDefinitions = manager.getAll();
  const sources = manager.getSources();

  const [directionFilter, setDirectionFilter] = useState<DirectionFilter>('all');
  const [sourceFilter, setSourceFilter] = useState<string>('all');

  const filtered = allDefinitions
    .filter((d: PipelineDefinition) =>
      directionFilter === 'all' ||
      d.metadata.direction === directionFilter ||
      d.metadata.direction === 'bidirectional'
    )
    .filter((d: PipelineDefinition) =>
      sourceFilter === 'all' || d.metadata.source === sourceFilter
    );

  const incomingCount   = allDefinitions.filter((d: PipelineDefinition) => d.metadata.direction === 'incoming').length;
  const outgoingCount   = allDefinitions.filter((d: PipelineDefinition) => d.metadata.direction === 'outgoing').length;
  const bidirCount      = allDefinitions.filter((d: PipelineDefinition) => d.metadata.direction === 'bidirectional').length;

  return (
    <ErrorBoundary>
      <div className="page-container hooks-subscribers-page">
        <div className="page-header">
          <h2>Pipeline Registry</h2>
          <p className="page-description">
            Complete list of registered pipelines with metadata, hooks, and subscribers configuration
        </p>
      </div>

      {/* Summary cards */}
      <section className="section">
        <h3>Pipeline Summary</h3>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
            gap: '16px',
          }}
        >
          {[
            { label: 'Total Pipelines', value: allDefinitions.length },
            { label: 'Incoming',        value: incomingCount },
            { label: 'Outgoing',        value: outgoingCount },
            { label: 'Bidirectional',   value: bidirCount },
          ].map(({ label, value }) => (
            <div
              key={label}
              style={{
                padding: '16px',
                background: 'rgba(15, 23, 42, 0.95)',
                borderRadius: '12px',
                border: '1px solid rgba(58, 140, 69, 0.4)',
                textAlign: 'center',
              }}
            >
              <p style={{ margin: '0 0 8px 0', fontSize: '12px', color: 'var(--text-secondary)', textTransform: 'uppercase' }}>
                {label}
              </p>
              <p style={{ margin: 0, fontSize: '24px', fontWeight: 700, color: 'var(--primary)' }}>
                {value}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* Pipelines Section */}
      <section className="section">
        <h3>Available Pipelines</h3>
        <p className="section-description">
          Pipelines are routing and processing configurations that define how messages flow through the system.
        </p>

        {/* Direction Filter */}
        <div className="category-filter">
          {(['all', 'incoming', 'outgoing', 'bidirectional'] as const).map((filter) => (
            <button
              key={filter}
              className={`filter-btn ${directionFilter === filter ? 'active' : ''}`}
              onClick={() => setDirectionFilter(filter)}
            >
              {filter.charAt(0).toUpperCase() + filter.slice(1)} ({
                filter === 'all'
                  ? allDefinitions.length
                  : filter === 'incoming' ? incomingCount
                  : filter === 'outgoing' ? outgoingCount
                  : bidirCount
              })
            </button>
          ))}
        </div>

        {/* Source Filter */}
        {sources.length > 0 && (
          <div className="category-filter" style={{ marginTop: '8px' }}>
            <button
              className={`filter-btn ${sourceFilter === 'all' ? 'active' : ''}`}
              onClick={() => setSourceFilter('all')}
            >
              All Sources ({allDefinitions.length})
            </button>
            {sources.map((source) => (
              <button
                key={source}
                className={`filter-btn ${sourceFilter === source ? 'active' : ''}`}
                onClick={() => setSourceFilter(source)}
              >
                {source} ({manager.getBySource(source).length})
              </button>
            ))}
          </div>
        )}

        {/* Pipelines Grid */}
        <div
          style={{
            display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
          gap: '20px',
        }}
      >
        {filtered.length === 0 ? (
          <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '40px 20px' }}>
            <p style={{ color: 'var(--text-secondary)', fontSize: '16px' }}>
              No pipelines found for {directionFilter === 'all' ? 'selected' : directionFilter} filter
            </p>
          </div>
        ) : (
          filtered.map((definition: PipelineDefinition) => (
            <PipelineCard key={definition.metadata.id} definition={definition} />
          ))
        )}
        </div>
        </section>
      </div>
    </ErrorBoundary>
  );
}
